// ============================================================================
// PROJECT GRAPH TAB
// Full-page knowledge graph view with actions for syncing session data,
// clearing graph, and rebuilding research data into Neo4j.
// ============================================================================

import React, { useState } from 'react';
import { Database, Loader2, CheckCircle, AlertTriangle, Trash2, Lightbulb, FileText, Shield, Camera, FolderRoot, Scale, Hammer, Bot } from 'lucide-react';
import { customerResearchService } from '../../services/sessionTasksService';
import { CustomerResearchGraph } from './CustomerResearchGraph';

interface ProjectGraphTabProps {
  projectId: string;
  token: string;
}

type ActionResult = {
  type: 'success' | 'error';
  message: string;
  stats?: Record<string, number>;
  errors?: string[];
};

export const ProjectGraphTab: React.FC<ProjectGraphTabProps> = ({
  projectId,
}) => {
  const [backfilling, setBackfilling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [backfillingIdeation, setBackfillingIdeation] = useState(false);
  const [backfillingTaskOutputs, setBackfillingTaskOutputs] = useState(false);
  const [backfillingGovernance, setBackfillingGovernance] = useState(false);
  const [backfillingProject, setBackfillingProject] = useState(false);
  const [backfillingDeviceImages, setBackfillingDeviceImages] = useState(false);
  const [backfillingDecisions, setBackfillingDecisions] = useState(false);
  const [backfillingBuilds, setBackfillingBuilds] = useState(false);
  const [backfillingAvatars, setBackfillingAvatars] = useState(false);
  const [backfillingProjectAvatars, setBackfillingProjectAvatars] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);
  const [graphKey, setGraphKey] = useState(0);

  const handleBackfill = async () => {
    setBackfilling(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillSessionsToGraph(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to start backfill',
      });
    } finally {
      setBackfilling(false);
    }
  };

  const handleClear = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setClearing(true);
    setConfirmClear(false);
    setActionResult(null);
    try {
      const result = await customerResearchService.clearGraph(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: { deletedNodes: result.deletedNodes },
      });
      // Force graph to re-render with fresh data
      setGraphKey(k => k + 1);
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to clear graph',
      });
    } finally {
      setClearing(false);
    }
  };

  const handleBackfillIdeation = async () => {
    setBackfillingIdeation(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillIdeation(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill ideation',
      });
    } finally {
      setBackfillingIdeation(false);
    }
  };

  const handleBackfillTaskOutputs = async () => {
    setBackfillingTaskOutputs(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillTaskOutputs(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill task outputs',
      });
    } finally {
      setBackfillingTaskOutputs(false);
    }
  };

  const handleBackfillGovernance = async () => {
    setBackfillingGovernance(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillGovernance(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill governance',
      });
    } finally {
      setBackfillingGovernance(false);
    }
  };

  const handleBackfillProject = async () => {
    setBackfillingProject(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillProject(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill project',
      });
    } finally {
      setBackfillingProject(false);
    }
  };

  const handleBackfillDeviceImages = async () => {
    setBackfillingDeviceImages(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillDeviceImages(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill device images',
      });
    } finally {
      setBackfillingDeviceImages(false);
    }
  };

  const handleBackfillDecisions = async () => {
    setBackfillingDecisions(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillDecisions(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill decisions',
      });
    } finally {
      setBackfillingDecisions(false);
    }
  };

  const handleBackfillBuilds = async () => {
    setBackfillingBuilds(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillBuilds(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill builds',
      });
    } finally {
      setBackfillingBuilds(false);
    }
  };

  const handleBackfillAvatars = async () => {
    setBackfillingAvatars(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillAvatars(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: result.stats,
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to backfill avatars',
      });
    } finally {
      setBackfillingAvatars(false);
    }
  };

  const handleBackfillProjectAvatars = async () => {
    setBackfillingProjectAvatars(true);
    setActionResult(null);
    try {
      const result = await customerResearchService.backfillProjectAvatars(projectId);
      setActionResult({
        type: 'success',
        message: result.message,
        stats: { count: result.count },
        errors: result.errors,
      });
    } catch (err: any) {
      setActionResult({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to sync project avatars',
      });
    } finally {
      setBackfillingProjectAvatars(false);
    }
  };

  const anyLoading = backfilling || clearing || backfillingIdeation || backfillingTaskOutputs || backfillingGovernance || backfillingProject || backfillingDeviceImages || backfillingDecisions || backfillingBuilds || backfillingAvatars || backfillingProjectAvatars;

  return (
    <div className="flex flex-col gap-6">
      {/* Action bar */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-navy">Graph Management</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Sync session data, rebuild research entities, or clear the graph.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Clear Graph */}
            {confirmClear ? (
              <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-2">
                <span className="text-xs text-red-700 font-medium">Delete all graph data?</span>
                <button
                  onClick={handleClear}
                  disabled={anyLoading}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmClear(false)}
                  className="rounded-lg bg-white border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleClear}
                disabled={anyLoading}
                className="flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {clearing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Clear Graph
                  </>
                )}
              </button>
            )}

          </div>
        </div>

        {/* Backfill buttons row */}
        <div className="flex items-center gap-2 pt-2 border-t border-[#e2e8f0]">
          <span className="text-xs font-medium text-slate-400 mr-1">Backfill:</span>

          {/* Backfill Sessions */}
          <button
            onClick={handleBackfill}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white shadow-sm shadow-primary/20 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfilling ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
            Sessions
          </button>

          {/* Backfill Ideation */}
          <button
            onClick={handleBackfillIdeation}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingIdeation ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
            Ideation
          </button>

          {/* Backfill Task Outputs */}
          <button
            onClick={handleBackfillTaskOutputs}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingTaskOutputs ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Tasks
          </button>

          {/* Backfill Project */}
          <button
            onClick={handleBackfillProject}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingProject ? <Loader2 size={14} className="animate-spin" /> : <FolderRoot size={14} />}
            Project
          </button>

          {/* Backfill Governance */}
          <button
            onClick={handleBackfillGovernance}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingGovernance ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
            Governance
          </button>

          {/* Backfill Device Images */}
          <button
            onClick={handleBackfillDeviceImages}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingDeviceImages ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            Device Images
          </button>

          {/* Backfill Decisions */}
          <button
            onClick={handleBackfillDecisions}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingDecisions ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
            Decisions
          </button>

          {/* Backfill Builds */}
          <button
            onClick={handleBackfillBuilds}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingBuilds ? <Loader2 size={14} className="animate-spin" /> : <Hammer size={14} />}
            Builds
          </button>

          {/* Backfill Avatars */}
          <button
            onClick={handleBackfillAvatars}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingAvatars ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
            Avatars
          </button>

          {/* Backfill Project Avatars */}
          <button
            onClick={handleBackfillProjectAvatars}
            disabled={anyLoading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillingProjectAvatars ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
            Project Avatars
          </button>
        </div>
      </div>

      {/* Action result */}
      {actionResult && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
          actionResult.type === 'success'
            ? 'bg-green-50 text-green-700 border-green-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {actionResult.type === 'success' ? <CheckCircle size={18} className="mt-0.5 shrink-0" /> : <AlertTriangle size={18} className="mt-0.5 shrink-0" />}
          <div className="flex-1">
            <p className="font-medium">{actionResult.message}</p>
            {actionResult.stats && (
              <div className="flex flex-wrap gap-4 mt-2 text-xs">
                {Object.entries(actionResult.stats).map(([key, val]) => (
                  <span key={key}><strong>{val}</strong> {key.replace(/([A-Z])/g, ' $1').toLowerCase().trim()}</span>
                ))}
              </div>
            )}
            {actionResult.errors && actionResult.errors.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs cursor-pointer font-medium flex items-center gap-1">
                  <AlertTriangle size={12} /> {actionResult.errors.length} warning{actionResult.errors.length !== 1 ? 's' : ''}
                </summary>
                <ul className="mt-1 text-xs space-y-0.5 pl-4 list-disc text-amber-700">
                  {actionResult.errors.slice(0, 10).map((e, i) => <li key={`${i}-${e}`}>{e}</li>)}
                  {actionResult.errors.length > 10 && <li>...and {actionResult.errors.length - 10} more</li>}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Graph visualization */}
      <section className="bg-white rounded-xl border border-[#e2e8f0]">
        <div className="p-5">
          <CustomerResearchGraph key={graphKey} projectId={projectId} allowMerge />
        </div>
      </section>
    </div>
  );
};

export default ProjectGraphTab;
